import Typography from "@portal/components/atoms/v2/typography";
import { MainBox } from "../components/mainBox";
import { IPageProps } from "@portal/types/robot/IPageProps";
import { RobotTextResources } from "@portal/assets/text/RobotTextResources";
import { useRobot } from "@portal/app/context/robotProvider";
import { RobotFooterComponent, RobotNavigateToPage } from "../components/footer";
import { TextLine } from "@portal/components/atoms/v2/textLine";
import { IRiskLevel } from "@portal/types/robot/IRiskLevel";
import Link from "next/link";
import Illustration from "@portal/components/atoms/illustrations";
import { SFDRInfo } from "./steps/sfdr";
import { PAIInfo } from "./steps/pai";
import { EUInfo } from "./steps/eu";
import { useState } from "react";
import { UserMessageComponent } from "../components/message/UserMessageComponent";
import { useSession } from "@portal/auth/client/SessionProvider";
import { useRouter } from "next/navigation";

const formatPreferenceList = (list: string[]) => {
  if (list.length <= 1) return list.join("");
  if (list.length === 2) return `${list[0]} og ${list[1]}`;
  return `${list.slice(0, -1).join(", ")} og ${list[list.length - 1]}`;
};

export const SustainabilityPreferencesPage = ({ pageIndex, centerId, isDefaultLanguage }: IPageProps) => {
  const {
    riskLevelRequest: request,
    setOriginallyWantedSustainabilitySfdr,
    setOriginallyWantedSustainabilityPai,
    setOriginallyWantedSustainabilityEu,
  } = useRobot();
  const isButtonEnabled = IsValidRequest(request);
  const texts = isDefaultLanguage ? RobotTextResources.primary : RobotTextResources.secondary;
  const [showUserMessage, setShowUserMessage] = useState(false);
  const [preferenceList, setPreferenceList] = useState([] as string[]);
  const router = useRouter();
  const { session } = useSession();
  const isLoggedIn = session?.status === "authenticated";

  const onNextClick = () => {
    setPreferenceList([]);
    if (IsAnyPreferencesSet(request)) {
      const prefList = [];
      if (request.sustainabilitySfdr === true) {
        setOriginallyWantedSustainabilitySfdr(true);
        prefList.push("SFDR");
      }
      if (request.sustainabilityPai === true) {
        setOriginallyWantedSustainabilityPai(true);
        prefList.push("PAI");
      }
      if (request.sustainabilityEu === true) {
        setOriginallyWantedSustainabilityEu(true);
        prefList.push("EU-taksonomi");
      }
      setPreferenceList(prefList);
      setShowUserMessage(true);
    } else {
      const newPageIndex = request.sustainability ? pageIndex + 1 : pageIndex + 2;
      const newUrl = RobotNavigateToPage(isLoggedIn ?? false, newPageIndex, centerId);
      router.push(newUrl);
    }
  };

  return (
    <>
      <MainBox>
        <Typography variant="Body">{texts.PAGE_7_INFO_HEADER}</Typography>
        <div className="flex h-52.5 justify-center mt-2">
          <Illustration name="sustainabilityIcon" size="image" />
        </div>
        <Typography variant="H6">{texts.PAGE_7_SUBHEADER}</Typography>
        <TextLine />
        <SFDRInfo isDefaultLanguage={isDefaultLanguage} />
        <PAIInfo isDefaultLanguage={isDefaultLanguage} />
        <EUInfo isDefaultLanguage={isDefaultLanguage} />
        <div className="flex my-2">
          <Link prefetch={false} href="https://norne.no/baerekraft/" target="_blank" className="flex underline items-start">
            <Typography variant="Bold">{texts.PAGE_7_LINK_TEXT}</Typography>
          </Link>
        </div>
      </MainBox>
      {showUserMessage && (
        <UserMessageComponent
          content={
            <div>
              <div>
                <Typography variant="H6">{texts.MESSAGE_MISSING_SUSTAINABILITY_RECOMMANDATIONS_TITLE}</Typography>
                <TextLine />
              </div>
              <Typography variant="Body">
                {texts.MESSAGE_MISSING_SUSTAINABILITY_RECOMMANDATIONS.replace(
                  "$PREFERENCE_LIST",
                  formatPreferenceList(preferenceList),
                )}
              </Typography>
            </div>
          }
          showDialog={showUserMessage}
          secondaryButtonText={texts.MESSAGE_CHANGE_REPLY}
          secondaryButtonClick={() => {
            setShowUserMessage(false);
          }}
        />
      )}
      <RobotFooterComponent
        pageIndex={pageIndex}
        centerId={centerId}
        primaryButtonText={texts.BUTTON_NEXT}
        secondaryButtonText={texts.BUTTON_BACK}
        disabled={!isButtonEnabled}
        primaryButtonClickOverride={onNextClick}
      />
    </>
  );
};

function IsValidRequest(request: IRiskLevel) {
  return request.sustainabilityEu !== null && request.sustainabilityPai !== null && request.sustainabilitySfdr !== null;
}

function IsAnyPreferencesSet(request: IRiskLevel) {
  return request.sustainabilityEu || request.sustainabilityPai || request.sustainabilitySfdr;
}
